import {sortVersions} from "@/utils/helpers";
const makeServices = ({i18n, arcologyClient, settings}) => ({
    loadArcologyConstants: async function(context, event) {
        return (await arcologyClient.get(`/constants`)).data;
    },

    loadStatisticsData: async function(context, event) {
        let statisticsApiUrl = `/logs/applications/statistics/top-applications-within-days/?top=88888&days=30`;
        if (event.payload) {
            statisticsApiUrl += `&projectId=${event.payload}`;
        }
        return (await arcologyClient.get(statisticsApiUrl)).data;
    },

    loadApplications: async function() {
        let applications = (
            await arcologyClient.get(`/warehouses/${settings.dataModels.DEFAULT_WAREHOUSE_ID}/applications`)
        ).data;
        for (let i = 0; i < applications.length; i++) {
            applications[i].versions.sort(sortVersions);
            applications[i].currentVersionIndex = 0;
        }
        return {applications};
    },

    updateFilters: async function(context, event) {
        const applicationsIds = event.payload.applications.map(element => element.id);
        const filtersCriteria = event.payload.filtersCriteria;
        return (await arcologyClient.post("/constants/filters", {applicationsIds, filtersCriteria})).data;
    },

    reloadFilters: async function(context, event) {
        let applications = (
            await arcologyClient.get(`/warehouses/${settings.dataModels.DEFAULT_WAREHOUSE_ID}/applications`)
        ).data;
        const applicationsIds = applications.map(element => element.id);
        const filtersCriteria = {};
        for (let key of Object.keys(context.filters)) {
            filtersCriteria[key] = null;
        }
        return (await arcologyClient.post("/constants/filters", {applicationsIds, filtersCriteria})).data;
    },

    loadDevices: async function() {
        // fetch devices
        return (await arcologyClient.get("/devices")).data;
    },

    loadProjects: async function() {
        // load projects
        const projects = (({data}) => data)(await arcologyClient.get("/projects"));
        let selectedProjects = [];
        for (let project of projects) {
            if (project.type === settings.dataModels.DEFAULT_PROJECT_TYPE) {
                selectedProjects.push((await arcologyClient.get(`/projects/${project.id}/global`)).data);
            }
        }
        return {
            projects: selectedProjects,
        };
    },

    loadSelectedProject: async function(context, event) {
        let selectedProject = (
            await arcologyClient.get(`/projects/${context.openApplicationsSelectedProjectId}/global`)
        ).data;
        return {
            project: selectedProject,
        };
    },

    loadWarehouses: async () => {
        return (await arcologyClient.get("/warehouses")).data;
    },

    saveApplication: async function(context, event) {
        const application = event.payload;
        await arcologyClient.patch(`/applications/${application.id}`, application);
        await Promise.all(
            application.versions.map(async version => {
                if (version.id) {
                    return (
                        await arcologyClient.patch(`/applications/${application.id}/versions/${version.id}`, version)
                    ).data;
                } else {
                    return (await arcologyClient.post(`/applications/${application.id}/versions`, [version])).data[0];
                }
            })
        );
        const updatedApplication = (await arcologyClient.get(`/applications/${application.id}`)).data;
        updatedApplication.versions = (await arcologyClient.get(`/applications/${application.id}/versions`)).data.sort(
            sortVersions
        );
        updatedApplication.currentVersionIndex = 0;
        return {updatedApplication};
    },

    saveDevice: async function(context, event) {
        const device = event.payload;
        await arcologyClient.patch(`/devices/${device.id}`, JSON.parse(JSON.stringify(device)));
    },

    deleteDevice: async function(context, event) {
        await arcologyClient.delete(`/devices/${event.payload.id}`);
        return event;
    },

    deleteApplication: async function(context, event) {
        const deletedApplication = (await arcologyClient.delete(`/applications/${event.payload.id}`)).data;
        return {deletedApplication};
    },

    deleteVersion: async function(context, event) {
        const {application, version} = event.payload;
        return await arcologyClient.delete(`/applications/${application.id}/versions/${version.id}`);
    },

    createApplication: async function(context, event) {
        let {application, warehouseId} = event.payload;
        if (!warehouseId && settings.dataModels.DEFAULT_WAREHOUSE_ID) {
            warehouseId = settings.dataModels.DEFAULT_WAREHOUSE_ID;
        }
        await arcologyClient.post(`/warehouses/${warehouseId}/applications`, [application]);
    },

    importApplications: async function(context, event) {
        let {applications, warehouseId} = event.payload;
        if (!warehouseId && settings.dataModels.DEFAULT_WAREHOUSE_ID) {
            warehouseId = settings.dataModels.DEFAULT_WAREHOUSE_ID;
        }
        if (warehouseId) {
            return (
                await arcologyClient.post(
                    `/warehouses/${warehouseId}/applications`,
                    Array.isArray(applications) ? applications : [applications]
                )
            ).data;
        }
    },

    createDevice: async function(context, event) {
        const device = event.payload;
        await arcologyClient.post("/devices", JSON.parse(JSON.stringify(device)));
        return event;
    },

    createProject: async function(context, event) {
        await arcologyClient.post("/projects", event.payload);
    },

    updateProject: async function(context, event) {
        await arcologyClient.patch(`/projects/${event.payload.id}`, event.payload);
    },

    createCategory: async function(context, event) {
        return (
            await arcologyClient.post(`/projects/${event.payload.project.id}/categories`, [
                {name: event.payload.category.name},
            ])
        ).data;
    },

    deleteProject: async function(context, event) {
        return (await arcologyClient.delete(`/projects/${event.payload.id}`)).data;
    },

    deleteCategory: async function(context, event) {
        return (
            await arcologyClient.delete(`/projects/${event.payload.project.id}/categories/${event.payload.category.id}`)
        ).data;
    },

    swapProjectCategories: async function(context, event) {
        await arcologyClient.put(`/projects/${event.payload.projectId}/categories`, event.payload.categories);
    },

    swapCategoryVersions: async function(context, event) {
        const {projectId, categoryId, versions} = event.payload;
        await arcologyClient.put(`/projects/${projectId}/categories/${categoryId}/versions`, versions);
    },

    async isLoggedIn() {
        // check via API call that we can access the arcology server
        await arcologyClient.get(`/devices`);
    },

    async login(context, event) {
        localStorage.setItem("ARCOLOGY.apiKey", event.payload.apiKey);
        // check via API call that we can access the arcology server
        await arcologyClient.get(`/devices`);
    },

    async upload(context, event) {
        const {formData, uploaderId} = event.payload;
        const uploadedFileData = (
            await arcologyClient.post(`/io/uploads`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            })
        ).data;
        return {
            uploaderId: uploaderId,
            url: uploadedFileData.url,
        };
    },

    warehousesStore: async (context, event) => {
        await arcologyClient.post("/warehouses", event.payload);
    },

    warehouseUpdate: async (context, event) => {
        await arcologyClient.patch(`/warehouses/${event.payload.id}`, event.payload);
    },

    warehouseDestroy: async (context, event) => {
        await arcologyClient.delete(`/warehouses/${event.payload.id}`);
    },
    warehouseImportApplications: async (context, event) => {
        await arcologyClient.post(
            `/warehouses/${event.payload.warehouse.id}/applications`,
            Array.isArray(event.payload.applications) ? event.payload.applications : [event.payload.applications]
        );
    },
    warehouseExportApplications: async (context, event) => {
        let applications = (await arcologyClient.get(`/warehouses/${event.payload.warehouse.id}/applications`)).data;
        for (let application of applications) {
            application.versions = (await arcologyClient.get(`/applications/${application.id}/versions`)).data;
        }
        return applications;
    },
});

export {makeServices};
